// EtiquetasPageXL - S47 / S48.1 / S48.2a / S48.2b.
// Layout 2 columnas (toolbar | canvas) + header (titulo + tamano + plantilla
// + producto + exportar) + seccion modo factura debajo del header.
//
// S48.2b agrega:
//   - Plantilla tecnica_bundle con auto-layout dedicado.
//   - Modo factura: cargar venta por id, forzar tecnica_individual,
//     ocultar ProductoPicker y deshabilitar SizePicker/TemplatePicker,
//     resolver lineas validas a productos y permitir export batch ZPL/PDF.
//   - Canvas preview = primera linea valida de la factura.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useEtiquetaEditor from './hooks/useEtiquetaEditor';
import useProductosEtiquetaXL from './hooks/useProductosEtiquetaXL';
import useFacturasEtiquetaXL from './hooks/useFacturasEtiquetaXL';
import usePlantillasXL from './hooks/usePlantillasXL';
import { findDynamicField } from './dynamicFields';
import { newFieldId, snapMm, clampMm, findSize } from './utils';
import {
  generarCamposTecnicosConAutoLayout,
  generarCamposTecnicosBundleConAutoLayout,
} from './autolayout';
import { logosMarcas } from './config/logosMarcas';
import SizePicker from './components/SizePicker';
import EditorToolbar from './components/EditorToolbar';
import EditorCanvas from './components/EditorCanvas';
import ProductoPicker from './components/ProductoPicker';
import TemplatePicker from './components/TemplatePicker';
import ExportMenu from './components/ExportMenu';
import FacturaPicker from './components/FacturaPicker';

const CSS_ID = 'xl-et-page-css';
const CSS = `
.xl-et-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-et-page__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; flex-wrap: wrap }
.xl-et-page__title-wrap { min-width: 0 }
.xl-et-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-et-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }
.xl-et-page__head-tools { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap }

.xl-et-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md);
  border: 1px solid var(--xl-color-border);
  background: transparent; color: var(--xl-color-text-secondary);
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1), border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-et-btn:hover { background: var(--xl-color-surface); color: var(--xl-color-text); border-color: rgba(46, 55, 69, 0.25) }
.xl-et-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-et-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-et-page__factura {
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 12px 16px; flex-wrap: wrap;
}
.xl-et-page__factura-label { font: 500 13px/1 var(--xl-font-sans); color: var(--xl-color-text-muted) }
.xl-et-page__factura-meta { font: 500 12px/1 var(--xl-font-mono); color: var(--xl-color-text-secondary) }
.xl-et-page__factura-warn { color: var(--xl-color-warning) }

.xl-et-page__body { display: grid; grid-template-columns: 240px 1fr; gap: 16px; align-items: start }
@media (max-width: 720px) { .xl-et-page__body { grid-template-columns: 1fr } }
`;

function _buildFieldsFromLayout(layoutBase, sizeId) {
  if (!Array.isArray(layoutBase)) return [];
  const sz = findSize(sizeId);
  const out = [];
  for (let i = 0; i < layoutBase.length; i += 1) {
    const entry = layoutBase[i];
    const k = entry && (entry.fieldKey || entry.key);
    if (!k) continue;
    const meta = findDynamicField(k);
    if (!meta) continue;
    const wMm = Number(entry.wMm) || meta.defaultWMm;
    const hMm = Number(entry.hMm) || meta.defaultHMm;
    const xMm = clampMm(snapMm(Number(entry.xMm) || 0), 0, Math.max(0, sz.wMm - wMm));
    const yMm = clampMm(snapMm(Number(entry.yMm) || 0), 0, Math.max(0, sz.hMm - hMm));
    out.push({
      id: newFieldId(),
      type: meta.type,
      bindKey: meta.bindKey,
      xMm: xMm, yMm: yMm, wMm: wMm, hMm: hMm,
    });
  }
  return out;
}

export default function EtiquetasPageXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const ed = useEtiquetaEditor();
  const productos = useProductosEtiquetaXL();
  const facturas = useFacturasEtiquetaXL();
  const plantillas = usePlantillasXL();
  const [productoId, setProductoId] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const lastTemplateRef = useRef(null);
  const lastTechRef = useRef({ tpl: null, prodId: null });

  const enModoFactura = facturas.factura != null;

  const template = useMemo(
    () => (templateId ? plantillas.findTemplate(templateId) : null),
    [templateId, plantillas]
  );

  const productoSeleccionado = useMemo(() => {
    if (productoId == null) return null;
    const arr = productos.items || [];
    for (let i = 0; i < arr.length; i += 1) {
      if (arr[i] && Number(arr[i].id) === Number(productoId)) return arr[i];
    }
    return null;
  }, [productoId, productos.items]);

  const productoCanvas = useMemo(() => {
    if (enModoFactura) {
      const arr = facturas.productosFactura || [];
      return arr.length > 0 ? arr[0] : null;
    }
    return productoSeleccionado;
  }, [enModoFactura, facturas.productosFactura, productoSeleccionado]);

  const bundleSeleccionado = useMemo(() => {
    if (!template) return null;
    if (template.dataKind !== 'bundle') return null;
    return template.demoData ? Object.assign({}, template.demoData) : null;
  }, [template]);

  // S48.2b: al cargar factura, forzar templateId='tecnica_individual'.
  useEffect(() => {
    if (enModoFactura && templateId !== 'tecnica_individual') {
      setTemplateId('tecnica_individual');
    }
  }, [enModoFactura, templateId]);

  // S48.2b: reset cache de auto-layout al cambiar factura.
  useEffect(() => {
    lastTechRef.current = { tpl: null, prodId: null };
  }, [facturas.factura]);

  // S48.2b: en modo factura sin productos validos -> canvas vacio.
  useEffect(() => {
    if (!enModoFactura) return;
    if ((facturas.productosFactura || []).length === 0) {
      ed.clearFields();
    }
  }, [enModoFactura, facturas.productosFactura, ed]);

  // S48.1: aplicar layoutBase de plantillas no-tecnicas.
  useEffect(() => {
    if (templateId === lastTemplateRef.current) return;
    lastTemplateRef.current = templateId;
    if (!template) return;
    if (template.id === 'tecnica_individual') return;
    if (template.id === 'tecnica_bundle') return;
    if (!Array.isArray(template.layoutBase) || template.layoutBase.length === 0) return;
    const newFields = _buildFieldsFromLayout(template.layoutBase, ed.sizeId);
    ed.setFields(newFields);
  }, [templateId, template, ed]);

  // S48.2a: auto-layout para tecnica_individual con productoCanvas.
  useEffect(() => {
    if (templateId !== 'tecnica_individual') return;
    if (!productoCanvas) return;
    const prodId = productoCanvas.id;
    const last = lastTechRef.current;
    if (last.tpl === templateId && last.prodId === prodId) return;
    lastTechRef.current = { tpl: templateId, prodId: prodId };
    if (!template || !Array.isArray(template.layoutBase)) return;
    const fields = generarCamposTecnicosConAutoLayout(productoCanvas, template.layoutBase, logosMarcas);
    if (template.sizeId && ed.sizeId !== template.sizeId) {
      ed.setSizeId(template.sizeId);
    }
    ed.setFields(fields);
  }, [templateId, productoCanvas, template, ed]);

  // S48.2b: auto-layout para tecnica_bundle con bundleSeleccionado (DEMO).
  useEffect(() => {
    if (templateId !== 'tecnica_bundle') return;
    if (!bundleSeleccionado) return;
    const bid = bundleSeleccionado.code || 'demo';
    const last = lastTechRef.current;
    if (last.tpl === templateId && last.prodId === bid) return;
    lastTechRef.current = { tpl: templateId, prodId: bid };
    if (!template || !Array.isArray(template.layoutBase)) return;
    const fields = generarCamposTecnicosBundleConAutoLayout(bundleSeleccionado, template.layoutBase, logosMarcas);
    if (template.sizeId && ed.sizeId !== template.sizeId) {
      ed.setSizeId(template.sizeId);
    }
    ed.setFields(fields);
  }, [templateId, bundleSeleccionado, template, ed]);

  const handleClear = () => ed.clearFields();
  const handleLoadFactura = (id) => facturas.cargarFactura(id);
  const handleClearFactura = () => facturas.quitarFactura();

  const exportState = useMemo(() => ({
    sizeId: ed.sizeId,
    fields: ed.fields,
  }), [ed.sizeId, ed.fields]);

  const allowedFields = template && Array.isArray(template.allowedFields)
    ? template.allowedFields : null;

  const tplTecIndividual = useMemo(
    () => plantillas.findTemplate('tecnica_individual'),
    [plantillas]
  );
  const batchLayoutBase = (tplTecIndividual && tplTecIndividual.layoutBase) || null;
  const batchSizeId = (tplTecIndividual && tplTecIndividual.sizeId) || 'tecnica-100x50';
  const productosFacturaArr = facturas.productosFactura || [];

  return (
    <div className="xl-et-page">
      <div className="xl-et-page__head">
        <div className="xl-et-page__title-wrap">
          <h1 className="xl-et-page__title">Etiquetas XL</h1>
          <p className="xl-et-page__subtitle">
            Editor de etiquetas Avery, termicas y tecnica. Plantillas tipadas, modo libre o modo factura.
          </p>
        </div>
        <div className="xl-et-page__head-tools">
          <TemplatePicker value={templateId} onChange={setTemplateId} disabled={enModoFactura} />
          <SizePicker value={ed.sizeId} onChange={ed.setSizeId} disabled={enModoFactura} />
          {!enModoFactura && (
            <ProductoPicker value={productoId} onChange={setProductoId} />
          )}
          <button type="button" className="xl-et-btn" onClick={handleClear} disabled={ed.fields.length === 0}>
            Limpiar
          </button>
          <ExportMenu
            state={exportState}
            producto={productoCanvas}
            bundle={bundleSeleccionado}
            factura={facturas.factura}
            productosFactura={productosFacturaArr}
            batchLayoutBase={batchLayoutBase}
            batchSizeId={batchSizeId}
            batchLogosConfig={logosMarcas}
          />
        </div>
      </div>

      <div className="xl-et-page__factura" aria-label="Modo factura">
        <span className="xl-et-page__factura-label">Modo factura:</span>
        <FacturaPicker
          factura={facturas.factura}
          loading={facturas.loading}
          error={facturas.error}
          onLoad={handleLoadFactura}
          onClear={handleClearFactura}
        />
        {enModoFactura && (
          <span className="xl-et-page__factura-meta">
            {productosFacturaArr.length} linea(s) valida(s)
            {productosFacturaArr.length === 0 && (
              <span className="xl-et-page__factura-warn">
                {' \u2014 sin lineas validas para etiquetas'}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="xl-et-page__body">
        <EditorToolbar allowedFields={allowedFields} />
        <EditorCanvas
          sizeId={ed.sizeId}
          fields={ed.fields}
          selectedId={ed.selectedId}
          onAddField={ed.addField}
          onSelectField={ed.selectField}
          producto={productoCanvas}
          bundle={bundleSeleccionado}
        />
      </div>
    </div>
  );
}
